import { PasswordLessAuthTypes } from '~/api/types/authMethodData.ts';
import {
  ConnectionData,
  ConnectionTypes,
  ScalekitInternalProviders,
  SocialProviders
} from '~/api/types/connectionData.ts';
import {
  addConnectionForEnvironment,
  ApiErrorInstance,
  toggleEnvironmentConnection,
  updateConnectionForEnvironment,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';

enum QuickstartFsaSteps {
  CONFIGURE = 'configure',
  TEST = 'test',
  INTEGRATE = 'integrate'
}

const resolvePasswordlessType = (
  otp: boolean,
  link: boolean
): PasswordLessAuthTypes | null => {
  if (otp && link) return PasswordLessAuthTypes.LINK_OTP;
  if (otp) return PasswordLessAuthTypes.OTP;
  if (link) return PasswordLessAuthTypes.LINK;
  return null; // disabled
};

const updatePasswordlessConnection = async (
  nextOtp: boolean,
  nextLink: boolean,
  connectionData: ConnectionData | null
): Promise<void> => {
  const nextType = resolvePasswordlessType(nextOtp, nextLink);
  const connection = connectionData;

  if (!connection) {
    if (nextType) {
      const connection = await addConnectionForEnvironment({
        type: ConnectionTypes.PASSWORDLESS,
        provider: ScalekitInternalProviders.SCALEKIT
      });
      await toggleEnvironmentConnection(connection.id, true);
    }
    return;
  }

  if (!nextType) {
    // both switches off → disable connection
    await toggleEnvironmentConnection(connection.id, false);
    return;
  }

  if (!connection.enabled) {
    await toggleEnvironmentConnection(connection.id, true);
  }

  const payload: ConnectionData = {
    ...connection,
    passwordlessConfig: { type: nextType }
  };

  await updateConnectionForEnvironment(
    connection.id,
    payload,
    ConnectionTypes.PASSWORDLESS
  );
};

const findConnection = ({
  connections,
  type,
  provider
}: {
  connections: ConnectionData[];
  type: ConnectionTypes;
  provider?: SocialProviders;
}): ConnectionData | undefined => {
  return connections.find(
    (c) => c.type === type && (provider ? c.provider === provider : true)
  );
};

const errorTranslationBasePath = 'server_errors.quickstart_fsa';

const handleConfigureLoginPageErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(
      `${errorTranslationBasePath}.configure_login_page_failed`,
      {
        intl
      }
    );
  }
});

export {
  QuickstartFsaSteps,
  updatePasswordlessConnection,
  handleConfigureLoginPageErrors,
  findConnection
};
